import React, { useState } from "react";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { ColorPicker, type ColorPreset } from "@cms/ui/components/color-picker";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@cms/ui/components/select";
import {
  TYPOGRAPHY_SCALES,
  TYPOGRAPHY_SCALE_MAX,
  TYPOGRAPHY_SCALE_MIN,
  type TokenField as TokenFieldSpec,
  type TokenGroup,
} from "../lib/design-tokens";
import { FontPicker } from "./font-picker";

const CUSTOM_SCALE = "custom";

/**
 * Typography scale editor: a dropdown of the predefined ratios plus "Custom",
 * next to a number input that is only editable when Custom is selected.
 */
function ScalePicker({ value, placeholder, onChange, ariaLabel }: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}) {
  const numeric = Number.parseFloat(value || placeholder);
  const preset = TYPOGRAPHY_SCALES.find((scale) => Math.abs(scale.value - numeric) < 0.0005);
  // Sticky so a typed custom ratio that happens to hit a preset value doesn't
  // snap the dropdown away from Custom mid-edit.
  const [customMode, setCustomMode] = useState(false);
  const isCustom = customMode || !preset;

  const onPick = (picked: string) => {
    if (picked === CUSTOM_SCALE) {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    const chosen = TYPOGRAPHY_SCALES.find((scale) => scale.label === picked);
    if (chosen) onChange(chosen.value.toFixed(3));
  };

  return (
    <div className="flex w-full items-center gap-2">
      <Select value={isCustom ? CUSTOM_SCALE : preset!.label} onValueChange={onPick}>
        <SelectTrigger className="flex-1 text-xs" aria-label={ariaLabel}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TYPOGRAPHY_SCALES.map((scale) => (
            <SelectItem key={scale.label} value={scale.label} className="text-xs">
              {scale.label} — {scale.value.toFixed(3)}
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM_SCALE} className="text-xs">
            Custom
          </SelectItem>
        </SelectContent>
      </Select>
      <Input
        type="number"
        step="0.001"
        min={TYPOGRAPHY_SCALE_MIN.toFixed(3)}
        max={TYPOGRAPHY_SCALE_MAX.toFixed(3)}
        value={value}
        placeholder={placeholder}
        disabled={!isCustom}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        className="w-24 shrink-0 text-xs"
        aria-label={`${ariaLabel} ratio`}
      />
    </div>
  );
}

const SELECT_DEFAULT = "__default__";

/** Options dropdown for an optional token, plus a "Default" choice that clears the override. */
function TokenSelectField({ value, placeholder, options, onChange, ariaLabel }: {
  value: string;
  placeholder: string;
  options: TokenFieldSpec["options"];
  onChange: (value: string) => void;
  ariaLabel: string;
}) {
  const defaultLabel = options?.find((o) => o.value === placeholder)?.label;
  return (
    <Select value={value || SELECT_DEFAULT} onValueChange={(v) => onChange(v === SELECT_DEFAULT ? "" : v)}>
      <SelectTrigger className="w-full text-xs" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={SELECT_DEFAULT} className="text-xs">
          Default{defaultLabel ? ` (${defaultLabel})` : ""}
        </SelectItem>
        {options?.map((option) => (
          <SelectItem key={option.value} value={option.value} className="text-xs">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function TokenField({ field, value, placeholder, presets, onChange }: {
  field: TokenFieldSpec;
  value: string;
  placeholder: string;
  presets: ColorPreset[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{field.label}</Label>
      <div className="flex items-center gap-2">
        {field.type === "color" && (
          <ColorPicker
            value={value || placeholder}
            onChange={onChange}
            presets={presets}
            ariaLabel={`${field.label} colour picker`}
          />
        )}
        {field.type === "font" ? (
          <FontPicker
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            ariaLabel={`${field.label} font picker`}
          />
        ) : field.type === "scale" ? (
          <ScalePicker
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            ariaLabel={`${field.label} picker`}
          />
        ) : field.type === "select" ? (
          <TokenSelectField
            value={value}
            placeholder={placeholder}
            options={field.options}
            onChange={onChange}
            ariaLabel={`${field.label} picker`}
          />
        ) : (
          <Input
            value={value}
            placeholder={placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            className={field.type === "color" ? "font-mono text-xs" : "text-xs"}
          />
        )}
      </div>
    </div>
  );
}

/** The token editor: one clearly-headed section per site area. */
export function TokenGroupGrid({ groups, overrides, defaults, presets, setToken }: {
  groups: TokenGroup[];
  overrides: Record<string, string>;
  defaults: Record<string, string>;
  presets: ColorPreset[];
  setToken: (key: string, value: string) => void;
}) {
  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.id} aria-label={group.label}>
          <h3 className="text-sm font-semibold">{group.label}</h3>
          {group.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{group.description}</p>
          )}
          <div className="mt-2.5 grid gap-3 border-t pt-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.fields.map((field) => (
              <TokenField
                key={field.key}
                field={field}
                value={overrides[field.key] ?? ""}
                placeholder={defaults[field.key] ?? ""}
                presets={presets}
                onChange={(v) => setToken(field.key, v)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
